'use client'

import { useEffect, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { motion } from 'motion/react'

import { Button, LottieAnimation } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { safeStringify } from '@helper/utils/data'
import { getStaticAssetUrl } from '@helper/utils/file'
import { buildUrl, formatDate } from '@helper/utils/format'
import { useHandleCopy } from '@helper/web/hooks'
import cn from '@config/tailwind/cn'

import { DownloadInvoiceButton } from './download-invoice-button'
import { useTriggerStatusMembership } from './use-trigger-status'

interface PaymentVerificationProps {
  triggerId: string
  type: 'web' | 'school'
  withInvoice?: boolean
  publicToken?: string
  /**
   * Called once the countdown below runs out. Optional: a caller that omits it gets the same button
   * as a plain link, with no countdown and no redirect.
   */
  onComplete?: () => void
}

/**
 * How long the success panel stays put before it moves the buyer on.
 *
 * Was 2500ms and unannounced, which read as the page navigating on its own: the seat count, the end
 * date and the invoice link are all on this panel, and a buyer barely got to see them. The button
 * counts down so the redirect is something that was announced rather than something that happened.
 */
const AUTO_REDIRECT_SECONDS = 5

export function PaymentVerification({
  triggerId,
  type,
  withInvoice,
  publicToken,
  onComplete,
}: PaymentVerificationProps) {
  const { status, error } = useTriggerStatusMembership(triggerId, { accessToken: publicToken })

  const { isCopied, handleCopy } = useHandleCopy()

  // Same success condition the render below falls through to: no error, trigger finished, output present.
  const isSuccess = !error && status.state !== 'error' && status.triggerState !== 'running' && Boolean(status.output)

  const [secondsLeft, setSecondsLeft] = useState(AUTO_REDIRECT_SECONDS)

  useEffect(() => {
    if (!onComplete || !isSuccess) return

    const interval = setInterval(() => setSecondsLeft((current) => Math.max(0, current - 1)), 1000)
    const timer = setTimeout(onComplete, AUTO_REDIRECT_SECONDS * 1000)

    return () => {
      clearInterval(interval)
      clearTimeout(timer)
    }
  }, [onComplete, isSuccess])

  // Where the buyer actually wants to land: the seats they just bought are useless until they are
  // handed to students, so both apps point at their own roster rather than at a home page.
  const manageLabel = type === 'school' ? 'Kelola murid' : 'Kelola anak'
  const manageHref =
    type === 'school'
      ? buildUrl('school', '/pengaturan/kelola-anggota/murid').href
      : buildUrl('web', '/dasbor', { tab: 'siswa-saya' }).href

  return (
    <main className='srd:w-full srd:max-w-[600px] srd:flex srd:items-center srd:justify-center srd:px-2'>
      {(() => {
        if (error || status.state === 'error') {
          return (
            <div className='srd:w-full srd:h-fit srd:flex srd:flex-col srd:items-center srd:justify-center'>
              <LottieAnimation
                animationConfig={{
                  path: getStaticAssetUrl('lottie/x-animation-lottiefiles.json'),
                  loop: false,
                  autoplay: true,
                }}
                style={{
                  width: '100%',
                  maxWidth: 'calc(var(--spacing) * 50)',
                  height: 'fit-content',
                }}
              />

              <div className='srd:flex srd:flex-col srd:gap-y-4'>
                <div className='srd:flex srd:flex-col srd:gap-y-0'>
                  <p
                    className={cn(
                      typographyVariants({ variant: 'title-lg' }),
                      'srd:text-neutral-black srd:text-center',
                    )}
                  >
                    Ada error saat memverifikasi pembayaran
                  </p>
                  <p
                    className={cn(
                      typographyVariants({ variant: 'title-lg' }),
                      'srd:text-neutral-black srd:text-center',
                    )}
                  >
                    Jangan khawatir, kami akan segera memperbaikinya
                  </p>
                </div>

                <div className='srd:flex srd:flex-col srd:gap-y-2'>
                  <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black srd:text-left')}>
                    Untuk mempercepat proses perbaikan error ini Silahkan hubungi kami melalui email{' '}
                    <a href='mailto:furqon@bacapibo.com' className='srd:text-blue-100'>
                      furqon@bacapibo.com
                    </a>{' '}
                    dengan subjek{' '}
                    <span className={typographyVariants({ variant: 'title-sm' })}>[Error] Verifikasi Pembayaran</span>{' '}
                    dan mencantumkan <em className='srd:italic'>error message</em> dibawah ini:
                  </p>

                  <div className='srd:bg-grey-20 srd:text-neutral-black srd:flex srd:h-fit srd:w-full srd:max-w-[1100px] srd:flex-col srd:items-center srd:justify-start srd:gap-y-2 srd:rounded-xl srd:p-2'>
                    <div className='srd:w-full srd:h-fit srd:flex srd:items-center srd:justify-between'>
                      <p className={typographyVariants({ variant: 'label-md' })}>Detil Error</p>

                      <Button
                        variant='link'
                        className='srd:text-blue-100 srd:text-nowrap srd:p-0 srd:w-fit srd:flex srd:items-center srd:justify-center srd:h-fit srd:hover:no-underline'
                        onClick={() => handleCopy(safeStringify(error ?? status).toString(), 'error')}
                        disabled={isCopied('error')}
                      >
                        {isCopied('error') ? (
                          <Check className='srd:w-4 srd:h-4 srd:mr-2 srd:shrink-0' />
                        ) : (
                          <Copy className='srd:w-4 srd:h-4 srd:mr-2 srd:shrink-0' />
                        )}
                        Salin Error
                      </Button>
                    </div>

                    {/* `font-mono` is the one place Poppins steps aside: this is a raw error
                        payload, and proportional type makes a JSON dump unreadable. */}
                    <pre
                      className={cn(
                        typographyVariants({ variant: 'body-sm' }),
                        'srd:font-mono srd:w-full srd:h-fit srd:overflow-auto srd:text-left srd:max-h-[300px] srd:overflow-y-auto',
                      )}
                    >
                      {(() => {
                        try {
                          if (error) {
                            return safeStringify(error).toString()
                          }

                          return safeStringify(status).toString()
                        } catch {
                          return '[Error displaying error details]'
                        }
                      })()}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        if (status.triggerState === 'running' || !status.output) {
          return (
            <div className='srd:w-full srd:h-fit srd:flex srd:flex-col srd:items-center srd:justify-center'>
              <LottieAnimation
                animationConfig={{
                  path: 'https://assets4.lottiefiles.com/packages/lf20_g0rr0uyo.json',
                  loop: true,
                  autoplay: true,
                }}
                style={{
                  width: '100%',
                  maxWidth: 'calc(var(--spacing) * 72)',
                  height: 'fit-content',
                }}
              />

              <div className='srd:flex srd:flex-col srd:gap-y-2 srd:text-center'>
                <p
                  className={cn(typographyVariants({ variant: 'title-lg' }), 'srd:text-neutral-black srd:text-center')}
                >
                  {status.label}
                </p>
                {/* `bg-neutrals-gray-300` was not a token at all — it resolved to nothing, so the
                    track behind the progress bar was invisible. */}
                <div className='srd:bg-grey-30 srd:h-px srd:w-full srd:rounded-full'>
                  <motion.div
                    className='srd:bg-blue-100 srd:h-full srd:w-full srd:rounded-full'
                    initial={{ width: '0%' }}
                    animate={{ width: `${status.progress}%` }}
                    transition={{ duration: 1 }}
                  />
                </div>
              </div>
            </div>
          )
        }

        return (
          <section className='srd:w-full srd:flex srd:flex-col srd:gap-y-8 srd:items-center srd:justify-center'>
            <img src={getStaticAssetUrl('logo/logo.svg')} alt='PiBo Logo' width={80} height={80} draggable={false} />

            <header className='srd:flex srd:flex-col srd:gap-y-2 srd:text-center'>
              <h1 className={cn(typographyVariants({ variant: 'headline-md' }), 'srd:text-neutral-black')}>
                Pembayaran Berhasil
              </h1>

              <h2 className={cn(typographyVariants({ variant: 'title-lg' }), 'srd:text-neutral-black')}>
                {(() => {
                  if (status.output.type === 'NEW') {
                    return `Kamu sekarang sudah menjadi ${type === 'school' ? 'Sekolah' : 'Keluarga'} Premium!`
                  }

                  if (status.output.type === 'ADD-ON') {
                    return 'Tambahan kursi berhasil ditambahkan!'
                  }

                  return `Perpanjangan ${type === 'school' ? 'Sekolah' : 'Keluarga'} Premium berhasil!`
                })()}
              </h2>
            </header>

            <section className='srd:w-full srd:flex srd:flex-col srd:gap-y-4'>
              <section className='srd:w-full srd:flex srd:items-center srd:justify-between srd:gap-x-4'>
                <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>
                  Jumlah kursi
                </p>

                <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-black')}>
                  {status.output.seats}
                </p>
              </section>

              <section className='srd:w-full srd:flex srd:items-center srd:justify-between srd:gap-x-4'>
                <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>
                  Berlaku hingga
                </p>

                <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-black')}>
                  {formatDate(status.output.membership.endDate, { dateStyle: 'long', timeStyle: 'short' })}
                </p>
              </section>

              {withInvoice && (
                <section className='srd:w-full srd:flex srd:items-center srd:justify-between srd:gap-x-4'>
                  <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>Invoice</p>

                  <DownloadInvoiceButton invoiceId={status.output.invoice.id} />
                </section>
              )}
            </section>

            <Button variant='gradient' asChild>
              {/* The countdown rides the label rather than a line of its own: the button is what the
                  buyer is looking at, and a timer elsewhere explains a redirect they have already
                  been surprised by. Clicking goes there immediately. */}
              <a href={manageHref}>{onComplete ? `${manageLabel} (${secondsLeft} detik)` : manageLabel}</a>
            </Button>
          </section>
        )
      })()}
    </main>
  )
}
